import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Shield, HeartPulse, Activity, Droplet, Wind, Thermometer, Scale, Plus, Trash2,
  ChevronLeft, ChevronRight, X, Loader2, TrendingUp, ArrowLeft, Lock, ScrollText, Pill,
  AlertTriangle, LifeBuoy, Settings2, Syringe,
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api from "../lib/api";
import { Spinner } from "../components/common";
import VaultGate, { isUnlocked, markUnlocked } from "../components/VaultGate";
import MedicationsView from "../components/vault/MedicationsView";
import AllergiesView from "../components/vault/AllergiesView";
import VaccinationsView from "../components/vault/VaccinationsView";
import EmergencyCardEditor from "../components/vault/EmergencyCardEditor";
import PrivacyView from "../components/vault/PrivacyView";
import AttachmentsPanel from "../components/vault/AttachmentsPanel";

/* /vault (canonical) — Safe Vault Home + Health Vault Phase 1.
   Reuses the existing VaultGate PIN/biometric lock (spec §42).
   No health values ever appear in URLs, analytics or the public feed (§4). */

const VITAL_META = {
  bp:          { label: "Blood Pressure",  Icon: HeartPulse, tint: "from-rose-100 to-white",  ic: "text-rose-600",   unit: "mmHg",   fields: [{k:"systolic",l:"Systolic"},{k:"diastolic",l:"Diastolic"},{k:"pulse",l:"Pulse (optional)"}] },
  hr:          { label: "Heart Rate",      Icon: Activity,   tint: "from-red-100 to-white",   ic: "text-red-600",    unit: "bpm",    fields: [{k:"bpm",l:"Beats per minute"}] },
  glucose:     { label: "Blood Glucose",   Icon: Droplet,    tint: "from-amber-100 to-white", ic: "text-amber-600",  unit: "mg/dL",  fields: [{k:"value",l:"Value"}] , units: ["mg/dL","mmol/L"] },
  spo2:        { label: "Blood Oxygen",    Icon: Wind,       tint: "from-sky-100 to-white",   ic: "text-sky-600",    unit: "%",      fields: [{k:"value",l:"SpO₂"}] },
  temperature: { label: "Temperature",     Icon: Thermometer,tint: "from-orange-100 to-white",ic: "text-orange-600", unit: "°F",     fields: [{k:"value",l:"Temperature"}], units: ["F","C"] },
  weight:      { label: "Weight",          Icon: Scale,      tint: "from-emerald-100 to-white",ic: "text-emerald-600",unit: "lb",    fields: [{k:"value",l:"Weight"}], units: ["lb","kg"] },
};

function summarize(kind, r) {
  if (!r) return "No reading yet";
  const f = r.fields || {};
  if (kind === "bp") return `${f.systolic}/${f.diastolic} ${f.unit || "mmHg"}${f.pulse ? ` · ${f.pulse} bpm` : ""}`;
  if (kind === "hr") return `${f.bpm} bpm`;
  if (kind === "spo2") return `${f.value}%`;
  return `${f.value} ${f.unit || ""}`;
}

function tsShort(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" }) + " · " + d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
}

/* -------- Add-Reading Dialog -------- */
function AddDialog({ kind, onClose, onSaved }) {
  const meta = VITAL_META[kind];
  const [form, setForm] = useState(() => {
    const init = {};
    (meta.fields || []).forEach((f) => { init[f.k] = ""; });
    init.unit = meta.units ? meta.units[0] : undefined;
    init.note = "";
    return init;
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    const fields = {};
    for (const f of meta.fields || []) {
      const v = parseFloat(form[f.k]);
      if (!Number.isFinite(v)) return toast.error(`Please enter ${f.l.toLowerCase()}`);
      fields[f.k] = v;
    }
    if (form.unit) fields.unit = form.unit;
    setBusy(true);
    try {
      await api.post("/vault/health/vitals", { kind, fields, note: form.note || null });
      toast.success("Reading saved");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save reading");
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-[9998] bg-zinc-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center"
         onClick={onClose} data-testid="vital-add-dialog">
      <div className="bg-white border border-zinc-200 shadow-2xl w-full sm:max-w-md sm:rounded-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-zinc-200">
          <div className="flex items-center gap-2">
            <meta.Icon className={`w-5 h-5 ${meta.ic}`} />
            <h3 className="font-heading font-800 text-lg">Add {meta.label} Reading</h3>
          </div>
          <button onClick={onClose} data-testid="vital-add-close" className="text-zinc-500 hover:text-destructive"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-3">
          {(meta.fields || []).map((f) => (
            <label key={f.k} className="block text-sm">
              <span className="font-600">{f.l}</span>
              <input type="number" step="any" inputMode="decimal" autoFocus={f === meta.fields[0]}
                value={form[f.k]} onChange={(e) => set(f.k, e.target.value)}
                data-testid={`vital-input-${f.k}`}
                className="mt-1 w-full border border-zinc-300 px-3 py-2 text-lg font-mono" />
            </label>
          ))}
          {meta.units && (
            <label className="block text-sm">
              <span className="font-600">Unit</span>
              <select value={form.unit} onChange={(e) => set("unit", e.target.value)}
                data-testid="vital-input-unit" className="mt-1 w-full border border-zinc-300 px-3 py-2">
                {meta.units.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </label>
          )}
          <label className="block text-sm">
            <span className="font-600">Notes <span className="font-400 text-muted-foreground">(optional)</span></span>
            <textarea value={form.note} onChange={(e) => set("note", e.target.value)} rows={2} maxLength={500}
              data-testid="vital-input-note" className="mt-1 w-full border border-zinc-300 px-3 py-2" />
          </label>
          <p className="text-[11px] text-muted-foreground leading-snug">
            My Health Vault does not diagnose disease and does not replace medical advice.
          </p>
        </div>
        <div className="flex items-center justify-end gap-2 p-4 border-t border-zinc-200">
          <button onClick={onClose} disabled={busy} className="px-4 py-2 border border-zinc-300 text-sm">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="vital-save"
            className="px-4 py-2 bg-emerald-600 text-white text-sm font-700 disabled:opacity-60 inline-flex items-center gap-1.5">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Save reading
          </button>
        </div>
      </div>
    </div>
  );
}

/* -------- Trend Chart -------- */
function TrendChart({ kind, items }) {
  const data = useMemo(() => (items || []).map((r) => {
    const f = r.fields || {};
    let y = f.value;
    if (kind === "bp") y = f.systolic;
    else if (kind === "hr") y = f.bpm;
    return { t: r.ts, y, y2: kind === "bp" ? f.diastolic : undefined };
  }), [items, kind]);

  if (!data.length) return <p className="text-sm text-muted-foreground">No readings in this window yet.</p>;
  return (
    <div className="h-56 w-full" data-testid={`trend-chart-${kind}`}>
      <ResponsiveContainer>
        <LineChart data={data}>
          <CartesianGrid stroke="#eee" strokeDasharray="3 3" />
          <XAxis dataKey="t" tickFormatter={(t) => new Date(t).toLocaleDateString(undefined, { month: "short", day: "numeric" })} fontSize={11} />
          <YAxis fontSize={11} />
          <Tooltip labelFormatter={(t) => new Date(t).toLocaleString()} />
          <Line type="monotone" dataKey="y" stroke="#059669" strokeWidth={2} dot={{ r: 2.5 }} name={kind === "bp" ? "Systolic" : VITAL_META[kind].label} />
          {kind === "bp" && <Line type="monotone" dataKey="y2" stroke="#dc2626" strokeWidth={2} dot={{ r: 2.5 }} name="Diastolic" />}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

/* -------- Vital Detail Panel -------- */
function VitalDetail({ kind, onBack, onChanged }) {
  const meta = VITAL_META[kind];
  const [range, setRange] = useState("30d");
  const [items, setItems] = useState(null);
  const [adding, setAdding] = useState(false);

  const load = () => api.get(`/vault/health/trends?kind=${kind}&window=${range}`)
    .then((r) => setItems(r.data.items || []))
    .catch(() => setItems([]));

  useEffect(() => { load(); }, [kind, range]);  // eslint-disable-line

  const del = async (vid) => {
    if (!window.confirm("Delete this reading permanently?")) return;
    try {
      await api.delete(`/vault/health/vitals/${vid}`);
      toast.success("Reading deleted");
      load();
      onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
  };

  return (
    <div data-testid={`vital-detail-${kind}`}>
      <div className="flex items-center gap-2 mb-4">
        <button onClick={onBack} data-testid="vital-detail-back" className="text-sm text-muted-foreground inline-flex items-center gap-1 hover:text-primary">
          <ChevronLeft className="w-4 h-4" /> Back
        </button>
      </div>

      <div className={`bg-gradient-to-br ${meta.tint} border border-zinc-200 p-5 mb-5`}>
        <div className="flex items-center gap-3">
          <meta.Icon className={`w-7 h-7 ${meta.ic}`} />
          <div className="flex-1">
            <h2 className="font-heading font-900 text-2xl">{meta.label}</h2>
            <p className="text-xs text-muted-foreground">{items?.length || 0} readings in this window</p>
          </div>
          <button onClick={() => setAdding(true)} data-testid={`vital-add-${kind}`}
            className="px-3 py-2 bg-emerald-600 text-white text-sm font-700 inline-flex items-center gap-1.5">
            <Plus className="w-4 h-4" /> Add
          </button>
        </div>
      </div>

      <div className="border border-zinc-200 bg-white p-4 mb-4">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="text-sm font-700 flex items-center gap-1.5"><TrendingUp className="w-4 h-4 text-emerald-600" /> Trend</div>
          <div className="flex gap-1" data-testid="trend-window-picker">
            {["7d","30d","3m","6m","1y","all"].map((w) => (
              <button key={w} onClick={() => setRange(w)} data-testid={`trend-window-${w}`}
                className={`px-2 py-1 text-xs font-700 border ${range === w ? "bg-emerald-600 text-white border-emerald-600" : "border-zinc-300 text-zinc-600 hover:bg-zinc-50"}`}>
                {w}
              </button>
            ))}
          </div>
        </div>
        {items === null ? <Spinner /> : <TrendChart kind={kind} items={items} />}
      </div>

      <div className="border border-zinc-200 bg-white mb-4">
        <div className="p-3 border-b border-zinc-100 text-sm font-700">History</div>
        {items === null ? <div className="p-5"><Spinner /></div>
          : items.length === 0 ? <p className="p-5 text-sm text-muted-foreground">No readings in this window. Tap Add above to record your first.</p>
          : items.slice().reverse().map((r) => (
            <VitalRowWithAttach key={r.id} r={r} kind={kind} onDelete={() => del(r.id)} />
          ))}
      </div>

      {adding && <AddDialog kind={kind} onClose={() => setAdding(false)} onSaved={() => { load(); onChanged && onChanged(); }} />}
    </div>
  );
}

function VitalRowWithAttach({ r, kind, onDelete }) {
  const [openAttach, setOpenAttach] = useState(false);
  return (
    <div className="border-b border-zinc-100 last:border-b-0" data-testid={`vital-row-${r.id}`}>
      <div className="flex items-center justify-between px-4 py-2.5">
        <div className="text-sm">
          <div className="font-700 text-zinc-900 font-mono">{summarize(kind, r)}</div>
          <div className="text-xs text-muted-foreground">{tsShort(r.ts)} · {r.source}{r.note ? ` · ${r.note}` : ""}</div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={() => setOpenAttach((v) => !v)} data-testid={`vital-attach-${r.id}`} title="Photos" className="text-zinc-400 hover:text-emerald-600 p-1.5">
            <span className="inline-block w-4 h-4"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/><circle cx="12" cy="13" r="3"/></svg></span>
          </button>
          <button onClick={onDelete} data-testid={`vital-delete-${r.id}`} className="text-zinc-400 hover:text-destructive p-1.5">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      {openAttach && (
        <div className="px-4 pb-3">
          <AttachmentsPanel parentType="vital" parentId={r.id} defaultTags={["vital", kind]} compact />
        </div>
      )}
    </div>
  );
}

/* -------- Home Dashboard -------- */
function VaultHome({ onOpen, onOpenMeds, onOpenSection }) {
  const [latest, setLatest] = useState(null);
  const [audit, setAudit] = useState(null);
  const [medCount, setMedCount] = useState(null);

  const load = () => {
    api.get("/vault/health/vitals?limit=200").then((r) => {
      const byKind = {};
      (r.data.items || []).forEach((v) => {
        if (!byKind[v.kind] || v.ts > byKind[v.kind].ts) byKind[v.kind] = v;
      });
      setLatest(byKind);
    }).catch(() => setLatest({}));
    api.get("/vault/health/audit?limit=8").then((r) => setAudit(r.data.items || [])).catch(() => setAudit([]));
    api.get("/vault/health/medications").then((r) => setMedCount((r.data.items || []).length)).catch(() => setMedCount(0));
  };

  useEffect(() => { load(); }, []);

  return (
    <div data-testid="health-vault-home">
      <div className="bg-gradient-to-br from-emerald-50 via-white to-sky-50 border border-zinc-200 p-5 mb-5">
        <div className="flex items-center gap-3">
          <Shield className="w-8 h-8 text-emerald-600 shrink-0" />
          <div className="flex-1">
            <div className="text-xs font-mono uppercase tracking-widest text-emerald-700">Safe Vault · Phase 1</div>
            <h1 className="font-heading font-900 text-2xl sm:text-3xl mt-1">My Health Vault</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Track · Protect · Understand · Share When You Choose
            </p>
          </div>
        </div>
      </div>

      <button onClick={onOpenMeds} data-testid="medications-card"
        className="w-full text-left bg-gradient-to-r from-emerald-100 to-white border border-emerald-200 p-4 mb-3 hover:shadow-md transition-shadow group flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-emerald-600 grid place-items-center shrink-0">
          <Pill className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1">
          <div className="font-heading font-800 text-sm">Medications</div>
          <div className="text-xs text-muted-foreground">
            {medCount === null ? "…" : medCount === 0 ? "No medications yet — tap to add" : `${medCount} active medication${medCount === 1 ? "" : "s"}`}
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-zinc-400 group-hover:text-emerald-600" />
      </button>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <button onClick={() => onOpenSection("allergies")} data-testid="allergies-card"
          className="text-left bg-gradient-to-br from-rose-100 to-white border border-rose-200 p-3 hover:shadow-md transition-shadow group">
          <div className="flex items-center gap-2 mb-1"><AlertTriangle className="w-5 h-5 text-rose-600" /><span className="font-heading font-800 text-sm">Allergies</span></div>
          <div className="text-[11px] text-muted-foreground">Tap to record and flag critical items</div>
        </button>
        <button onClick={() => onOpenSection("emergency")} data-testid="emergency-card-tile"
          className="text-left bg-gradient-to-br from-orange-100 to-white border border-orange-200 p-3 hover:shadow-md transition-shadow group">
          <div className="flex items-center gap-2 mb-1"><LifeBuoy className="w-5 h-5 text-orange-600" /><span className="font-heading font-800 text-sm">Emergency Card</span></div>
          <div className="text-[11px] text-muted-foreground">Pick fields, share with a link</div>
        </button>
      </div>

      <button onClick={() => onOpenSection("vaccinations")} data-testid="vaccinations-card"
        className="w-full text-left bg-gradient-to-r from-cyan-100 to-white border border-cyan-200 p-4 mb-4 hover:shadow-md transition-shadow group flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-cyan-600 grid place-items-center shrink-0">
          <Syringe className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1">
          <div className="font-heading font-800 text-sm">Vaccinations</div>
          <div className="text-xs text-muted-foreground">Record shots, boosters, travel requirements &amp; card photos</div>
        </div>
        <ChevronRight className="w-5 h-5 text-zinc-400 group-hover:text-cyan-600" />
      </button>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
        {Object.entries(VITAL_META).map(([kind, meta]) => {
          const r = latest?.[kind];
          return (
            <button key={kind} onClick={() => onOpen(kind)} data-testid={`vital-card-${kind}`}
              className={`text-left bg-gradient-to-br ${meta.tint} border border-zinc-200 p-4 hover:shadow-md transition-shadow group`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <meta.Icon className={`w-5 h-5 ${meta.ic}`} />
                  <span className="font-heading font-800 text-sm">{meta.label}</span>
                </div>
                <ChevronRight className="w-4 h-4 text-zinc-400 group-hover:text-emerald-600" />
              </div>
              <div className="text-lg font-mono font-700 text-zinc-900">
                {latest === null ? "…" : summarize(kind, r)}
              </div>
              <div className="text-[11px] text-muted-foreground mt-0.5">
                {r ? tsShort(r.ts) : "Tap to add your first reading"}
              </div>
            </button>
          );
        })}
      </div>

      <div className="border border-zinc-200 bg-white mb-6" data-testid="health-vault-audit">
        <div className="p-3 border-b border-zinc-100 text-sm font-700 flex items-center gap-1.5">
          <ScrollText className="w-4 h-4 text-emerald-600" /> Recent activity
        </div>
        {audit === null ? <div className="p-4"><Spinner /></div>
          : audit.length === 0 ? <p className="p-4 text-sm text-muted-foreground">Your vault is empty — activity will appear here as you use it.</p>
          : audit.map((a) => (
            <div key={a.id || a.ts} className="px-4 py-2 border-b border-zinc-100 last:border-b-0 text-sm flex items-center justify-between">
              <span className="text-zinc-800">{a.event}</span>
              <span className="text-xs text-muted-foreground font-mono">{tsShort(a.ts)}</span>
            </div>
          ))}
      </div>

      <div className="border border-emerald-200 bg-emerald-50/60 p-4 text-xs text-emerald-900 leading-relaxed" data-testid="health-vault-privacy-note">
        <div className="flex items-start gap-2">
          <Lock className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <strong>Private by default.</strong> Your Health Vault is isolated from public profiles,
            Social Capital, advertising, analytics, chats and search. It is never used for ads.
            Only you can view it — until you explicitly choose to share.
          </div>
        </div>
      </div>

      <button onClick={() => onOpenSection("privacy")} data-testid="privacy-card"
        className="mt-4 w-full text-left border border-zinc-200 bg-white p-3 hover:bg-zinc-50 flex items-center gap-3">
        <Settings2 className="w-5 h-5 text-zinc-500" />
        <div className="flex-1"><div className="font-heading font-800 text-sm">Privacy &amp; Data</div><div className="text-xs text-muted-foreground">Export, view activity, delete your vault</div></div>
        <ChevronRight className="w-4 h-4 text-zinc-400" />
      </button>
    </div>
  );
}

/* -------- Page shell + unlock gate -------- */
export default function SafeVault() {
  const nav = useNavigate();
  const [unlocked, setUnlocked] = useState(() => isUnlocked());
  const [authed, setAuthed] = useState(null);   // null=checking, true=signed-in, false=guest
  const [openKind, setOpenKind] = useState(null);
  const [openMeds, setOpenMeds] = useState(false);
  const [section, setSection] = useState(null);   // "allergies" | "emergency" | "privacy"

  useEffect(() => {
    document.title = "My Health Vault | Safe Vault · TravelerPulse";
    window.scrollTo(0, 0);
    // Auth guard — Safe Vault requires a signed-in user (§49 server-side check,
    // but we short-circuit here to avoid a jarring 401 flash on the VaultGate).
    api.get("/auth/me").then(() => setAuthed(true)).catch(() => setAuthed(false));
  }, []);

  if (authed === null) {
    return <div className="max-w-3xl mx-auto px-4 py-16 text-center"><Spinner /></div>;
  }
  if (authed === false) {
    return (
      <div className="max-w-md mx-auto px-4 py-16 text-center" data-testid="safe-vault-signin-required">
        <Shield className="w-12 h-12 mx-auto text-emerald-600 mb-3" />
        <h1 className="font-heading font-900 text-2xl mb-2">Sign in to open Safe Vault</h1>
        <p className="text-sm text-muted-foreground mb-4">Your Health Vault is private to your account. Sign in to unlock it.</p>
        <Link to={`/login?next=${encodeURIComponent(window.location.pathname)}`}
          data-testid="safe-vault-signin-cta"
          className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-emerald-600 text-white font-700">
          Sign in
        </Link>
      </div>
    );
  }

  if (!unlocked) {
    return <VaultGate
      title="Safe Vault"
      subtitle="Create a PIN to protect your Health Vault"
      scope="safe_vault"
      onUnlock={() => { markUnlocked(); setUnlocked(true); }}
      onClose={() => nav(-1)} />;
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6" data-testid="safe-vault-page">
      <div className="flex items-center justify-between mb-3 text-sm">
        <Link to="/app" data-testid="safe-vault-back" className="text-muted-foreground hover:text-primary inline-flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Back to My Social Pulse
        </Link>
        <span className="text-xs text-muted-foreground font-mono uppercase tracking-wider">Locked when you close the tab</span>
      </div>
      {openKind ? <VitalDetail kind={openKind} onBack={() => setOpenKind(null)} />
        : openMeds ? <MedicationsView onBack={() => setOpenMeds(false)} />
        : section === "allergies" ? <AllergiesView onBack={() => setSection(null)} />
        : section === "vaccinations" ? <VaccinationsView onBack={() => setSection(null)} />
        : section === "emergency" ? <EmergencyCardEditor onBack={() => setSection(null)} />
        : section === "privacy" ? <PrivacyView onBack={() => setSection(null)} onVaultDeleted={() => { setSection(null); }} />
        : <VaultHome onOpen={setOpenKind} onOpenMeds={() => setOpenMeds(true)} onOpenSection={setSection} />}
      <p className="mt-6 text-center text-[11px] text-muted-foreground">My Social Pulse · Powered by TravelerPulse.pro</p>
    </div>
  );
}
